import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHandshake, 
  faChartLine, 
  faUsers,
  faLandmark
} from '@fortawesome/free-solid-svg-icons';

import CallToAction from '../components/sections/CallToAction';

const PageBanner = styled.section`
  background: linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.7)), url('/images/zaraq-iqbal-3SOTbC6_NlA-unsplash.jpg');
  background-size: cover;
  background-position: center;
  padding: 120px 0 80px;
  text-align: center;
  color: #fff;
  
  h1 {
    font-size: 3rem;
    font-weight: 800;
    margin-bottom: 20px;
    
    span {
      color: #ff9800;
    }
    
    @media (max-width: 768px) {
      font-size: 2.3rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    max-width: 700px;
    margin: 0 auto 30px;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
`;

const AboutSection = styled.section`
  padding: 100px 0;
  background-color: #fff;
  
  .about-content {
    h2 {
      font-size: 2.5rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 25px;
      
      span {
        color: #2e7d32;
      }
    }
    
    p {
      color: #666;
      font-size: 1.1rem;
      line-height: 1.8;
      margin-bottom: 25px;
    }
    
    .highlight {
      color: #2e7d32;
      font-weight: 600;
    }
  }
  
  .about-image {
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
    
    img {
      width: 100%;
      height: auto;
      transition: transform 0.5s ease;
      
      &:hover {
        transform: scale(1.03);
      }
    }
    
    @media (max-width: 992px) {
      margin-top: 40px;
    }
  }
`;

const ValuesSection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
  
  .section-title {
    text-align: center;
    margin-bottom: 60px;
    
    h2 {
      font-size: 2.5rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 20px;
      
      span {
        color: #2e7d32;
      }
    }
    
    p {
      color: #666;
      font-size: 1.1rem;
      max-width: 700px;
      margin: 0 auto;
    }
  }
  
  .value-card {
    height: 100%;
    border: none;
    border-radius: 15px;
    padding: 40px 30px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    text-align: center;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
    }
    
    .value-icon {
      width: 90px;
      height: 90px;
      border-radius: 50%;
      background-color: rgba(46, 125, 50, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 2.5rem;
      color: #2e7d32;
      margin: 0 auto 25px;
    }
    
    .card-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 20px;
    }
    
    .card-text {
      color: #666;
      font-size: 1rem;
      line-height: 1.7;
    }
  }
`;

const TeamSection = styled.section`
  padding: 100px 0;
  background-color: #fff;
  
  .section-title {
    text-align: center;
    margin-bottom: 60px;
    
    h2 {
      font-size: 2.5rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 20px;
      
      span {
        color: #2e7d32;
      }
    }
    
    p {
      color: #666;
      font-size: 1.1rem;
      max-width: 700px;
      margin: 0 auto;
    }
  }
  
  .team-card {
    height: 100%;
    border: none;
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-10px);
      box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
      
      .card-img-top {
        transform: scale(1.05);
      }
    }
    
    .card-img-wrapper {
      height: 300px;
      overflow: hidden;
      
      .card-img-top {
        height: 100%;
        width: 100%;
        object-fit: cover;
        transition: transform 0.5s ease;
      }
    }
    
    .card-body {
      text-align: center;
      padding: 25px;
    }
    
    .card-title {
      font-weight: 700;
      font-size: 1.4rem;
      margin-bottom: 5px;
      color: #333;
    }
    
    .position {
      color: #2e7d32;
      font-weight: 600;
      margin-bottom: 15px;
    }
    
    .card-text {
      color: #666;
      font-size: 1rem;
      line-height: 1.6;
    }
    
    .social-links {
      margin-top: 20px;
      display: flex;
      justify-content: center;
      gap: 15px;
      
      a {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        background-color: #f5f5f5;
        color: #666;
        border-radius: 50%;
        transition: all 0.3s ease;
        
        &:hover {
          background-color: #2e7d32;
          color: #fff;
          transform: translateY(-3px);
        }
      }
    }
  }
`;

const StatsSection = styled.section`
  padding: 80px 0;
  background: linear-gradient(to right, #333, #222);
  color: #fff;
  
  .stats-container {
    text-align: center;
    
    .stat-item {
      margin-bottom: 15px;
      
      @media (max-width: 992px) {
        margin-bottom: 40px;
      }
      
      .stat-icon {
        font-size: 2.5rem;
        color: #ff9800;
        margin-bottom: 15px;
      }
      
      .stat-number {
        font-size: 3rem;
        font-weight: 800;
        margin-bottom: 10px;
      }
      
      .stat-label {
        font-size: 1.2rem;
        opacity: 0.8;
      }
    }
  }
`;

const About = () => {
  const values = [
    {
      icon: faHandshake,
      title: "Integrity",
      description: "We operate with honesty and transparency in every transaction. You can trust us to provide to provide cash offer for your land fast and handle the entire sale process with integrity."
    },
    {
      icon: faChartLine,
      title: "Excellence",
      description: "Our team is committed to excellence in customer service and process efficiency. We constantly refine our methods to ensure a smooth, professional experience for every landowner we work with."
    },
    {
      icon: faUsers,
      title: "Customer Focus",
      description: "Your needs and circumstances are unique, and we recognize that. We listen carefully to understand your situation and create personalized solutions that work for you."
    },
    {
      icon: faLandmark,
      title: "Reliability",
      description: "When we make a commitment, we stand by it. You can count on us to follow through with our promises, meet deadlines, and close quickly as agreed upon."
    }
  ];

  // Team section removed as requested

  return (
    <>
      <PageBanner>
        <Container>
          <h1>About <span>Us</span></h1>
          <p>
            We're a team of land investment professionals dedicated to providing 
            landowners with a simple, fair way to sell their properties.
          </p>
        </Container>
      </PageBanner>
      
      <AboutSection>
        <Container>
          <Row className="align-items-center">
            <Col lg={12}>
              <div className="about-content">
                <h2>About <span>Bering Solutions</span></h2>
                <p>
                  Bering Solutions is a professional land buying company with a mission to make selling land 
                  simple, fast, and fair. With years of experience in the real estate industry, we've helped 
                  hundreds of property owners nationwide sell their land with ease and confidence.
                </p>
                <p>
                  We specialize in purchasing all types of vacant land throughout the United States — from 
                  rural acreage to development parcels and commercial land — regardless of condition or 
                  location.
                </p>
                <p>
                  What sets us apart is our commitment to creating <span className="highlight">win-win solutions</span>. 
                  We understand that each property owner's situation is unique, and we tailor our approach 
                  accordingly. Whether you've inherited unwanted land, own property in a distant location, 
                  face challenging tax situations, or simply want a quick, hassle-free sale, we're here to 
                  help.
                </p>
                <p>
                  Our process eliminates the traditional headaches of selling land — no commissions, no 
                  listing fees, no lengthy waiting periods, and no complicated paperwork. Instead, we offer
                  <span className="highlight"> fair cash offers</span>, flexible closing timelines, and a 
                  professional experience from start to finish.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </AboutSection>
      
      <ValuesSection>
        <Container>
          <div className="section-title">
            <h2>Our Core <span>Values</span></h2>
            <p>
              These principles guide everything we do and define how we conduct business with our clients.
            </p>
          </div>
          
          <Row className="g-4">
            {values.map((value, index) => (
              <Col key={index} md={6} lg={3}>
                <Card className="value-card">
                  <div className="value-icon">
                    <FontAwesomeIcon icon={value.icon} />
                  </div>
                  <Card.Body className="p-0">
                    <Card.Title>{value.title}</Card.Title>
                    <Card.Text>{value.description}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </ValuesSection>
      
      <StatsSection>
        <Container>
          <Row className="justify-content-center">
            <Col md={4} lg={4}>
              <div className="stats-container">
                <div className="stat-item">
                  <div className="stat-icon">
                    <i className="fas fa-map-marked-alt"></i>
                  </div>
                  <div className="stat-number">50</div>
                  <div className="stat-label">States Served</div>
                </div>
              </div>
            </Col>
            <Col md={4} lg={4}>
              <div className="stats-container">
                <div className="stat-item">
                  <div className="stat-icon">
                    <i className="fas fa-user-check"></i>
                  </div>
                  <div className="stat-number">100%</div>
                  <div className="stat-label">Client Satisfaction</div>
                </div>
              </div>
            </Col>
            <Col md={4} lg={4}>
              <div className="stats-container">
                <div className="stat-item">
                  <div className="stat-icon">
                    <i className="fas fa-calendar-check"></i>
                  </div>
                  <div className="stat-number">10+</div>
                  <div className="stat-label">Years Experience</div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </StatsSection>
      
      {/* Meet Our Team section removed as requested */}
      
      <CallToAction 
        title="Have Questions About Our Company?"
        subtitle="Contact us today to learn more about who we are and how we can help you sell your land quickly for a fair cash price."
        primaryButtonText="Contact Us"
        secondaryButtonText="Get a Cash Offer"
        primaryButtonLink="/contact"
        secondaryButtonLink="/get-offer"
      />
    </>
  );
};

export default About;
